/**
 * Self-healing cleanup for stale service workers and precache entries.
 *
 * Root cause: older CRA deploys registered a Workbox-generated service worker
 * that precached /static/ assets. The current build no longer ships a service
 * worker, but previously visiting users still have the stale registration.
 * Their browser serves expired cache entries ("Resource was not cached").
 *
 * This runs once on every page load. If it finds any service worker
 * registrations or Workbox caches it removes them and reloads once.
 */
export function cleanupStaleServiceWorkers() {
  if (!("serviceWorker" in navigator)) return;

  const RELOAD_KEY = "sw-cleanup-reload";

  // Prevent infinite reload loops — only reload once per session
  if (sessionStorage.getItem(RELOAD_KEY)) {
    sessionStorage.removeItem(RELOAD_KEY);
    return;
  }

  navigator.serviceWorker.getRegistrations().then(async (registrations) => {
    let didCleanup = false;

    // Unregister every service worker
    for (const reg of registrations) {
      await reg.unregister();
      didCleanup = true;
    }

    // Purge all caches (Workbox precache + runtime caches)
    if ("caches" in window) {
      const keys = await caches.keys();
      for (const key of keys) {
        await caches.delete(key);
        didCleanup = true;
      }
    }

    // Reload once so the browser fetches fresh assets from the server
    if (didCleanup) {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
    }
  });
}
